// @ts-check
// lib/scope.js — lógica de "qué pertenece a quién" compartida entre el
// backend (Node CJS) y el frontend (esbuild bundle, CJS→ESM auto).
//
// Por qué existe: lotes 102 y 107 fueron bugs causados por filtrar deals
// por equipo del comercial (owner.teamId) en algunas vistas mientras otras
// ya filtraban por equipo de la venta (deal.verticalTeamId). El mismo
// número aparecía distinto en Inicio vs Ventas vs Cobros, según qué
// función de scope usara cada pantalla.
//
// Solución: una sola definición autoritativa de cada concepto. Cualquier
// consumer que filtre por equipo / periodo / rol-en-mes usa estas
// funciones — no roll-your-own.

'use strict';

/**
 * Equipo del comercial para un mes concreto. Si el user tiene
 * `monthlyTeams['YYYY_M']` (override histórico), gana sobre `teamId`.
 * Permite que un comercial cambie de equipo y los listados/cálculos
 * respeten el equipo que tenía en ese mes (no el actual).
 *
 * @param {{ monthlyTeams?: Record<string, string>, teamId?: string|null }|null|undefined} user
 * @param {number|null|undefined} year
 * @param {number|null|undefined} month
 * @returns {string|null}
 */
function userTeamForMonth(user, year, month) {
  if (!user) return null;
  if (year != null && month != null) {
    const ov = user.monthlyTeams && user.monthlyTeams[year + '_' + month];
    if (ov) return ov;
  }
  return user.teamId || null;
}

/**
 * Rol del comercial para un mes concreto. Mismo patrón que userTeamForMonth.
 * Permite que un comercial pasado a manager_comercial conserve el histórico
 * de cuando era comercial (rankings, ventas, comisiones).
 */
function userRoleForMonth(user, year, month) {
  if (!user) return null;
  if (year != null && month != null) {
    const ov = user.monthlyRoles && user.monthlyRoles[year + '_' + month];
    if (ov) return ov;
  }
  return user.role || null;
}

/**
 * Tramo/nivel del comercial para un mes concreto. Mismo patrón.
 */
function userLevelForMonth(user, year, month) {
  if (!user) return null;
  if (year != null && month != null) {
    const ov = user.monthlyLevels && user.monthlyLevels[year + '_' + month];
    if (ov) return ov;
  }
  return user.levelId || null;
}

/**
 * Plan/categoría del comercial para un mes concreto.
 */
function userCategoryForMonth(user, year, month) {
  if (!user) return null;
  if (year != null && month != null) {
    const ov = user.monthlyCategories && user.monthlyCategories[year + '_' + month];
    if (ov) return ov;
  }
  return user.defaultCategoryId || null;
}

/**
 * dealTeamId — el equipo de la VENTA. Esta es LA función que arregla los
 * bugs de inconsistencia entre Inicio / Ventas / Cobros / Stats.
 *
 * Prioridad:
 *   1. `deal.verticalTeamId` (propiedad "Vertical by Ratings" de HubSpot
 *      capturada en lote-100). Incluso si es `null` explícitamente — eso
 *      significa WorkCamp, una vertical excluida deliberadamente.
 *   2. Fallback a `userTeamForMonth(user, y, m)` para deals que no traen
 *      vertical (deals previos a lote-100, ~0.1% del año actual).
 *
 * @param {object|null|undefined} deal
 * @param {object|null|undefined} user
 * @param {number|null|undefined} year
 * @param {number|null|undefined} month
 * @returns {string|null}
 */
function dealTeamId(deal, user, year, month) {
  if (deal && Object.prototype.hasOwnProperty.call(deal, 'verticalTeamId')) {
    return deal.verticalTeamId; // honor null explícito (WorkCamp)
  }
  return userTeamForMonth(user, year, month);
}

/**
 * Devuelve true si un closedate ISO cae dentro de [start, end] (inclusivo).
 * Comparación lexicográfica sobre el prefijo YYYY-MM-DD — funciona porque
 * ISO 8601 lexicographic order coincide con cronológico.
 *
 * @param {string|null|undefined} iso  closedate o cualquier ISO 8601
 * @param {string} startIso  YYYY-MM-DD
 * @param {string} endIso    YYYY-MM-DD
 */
function inPeriod(iso, startIso, endIso) {
  if (!iso) return false;
  const d = iso.slice(0, 10);
  return d >= startIso && d <= endIso;
}

module.exports = {
  userTeamForMonth,
  userRoleForMonth,
  userLevelForMonth,
  userCategoryForMonth,
  dealTeamId,
  inPeriod,
};
